import { useCallback, useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { Pencil, RefreshCw, Save, X } from "lucide-react";
import { companyService, employeeService } from "../../services/api";
import { getAuthJson, setAuthJson } from "../../utils/authStorage";
import { usePermissions } from "../../rbac/PermissionsContext";
import { DsInput, DsLabel, DsPageHeader } from "../../design-system";
import SearchableSelect from "./SearchableSelect.jsx";

/** Indian TAN: 4 letters + 5 digits + 1 letter (e.g. BLRM12345E) */
export const TAN_REGEX = /^[A-Z]{4}[0-9]{5}[A-Z]$/;
/** Indian PAN: 5 letters + 4 digits + 1 letter */
export const PAN_REGEX = /^[A-Z]{5}[0-9]{4}[A-Z]$/;

const EMPTY_FORM = {
  companyName: "",
  address: "",
  email: "",
  mobile: "",
  pfCode: "",
  esiCode: "",
  labourLicense: "",
  domainName: "",
  contactPerson: "",
  website: "",
  panNo: "",
  tanNo: "",
  password: "",
  logo: null,
  headOfOrganisationEmployeeId: null,
};

export function cleanTan(raw) {
  return String(raw || "")
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, "")
    .slice(0, 10);
}

export function cleanPan(raw) {
  return String(raw || "")
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, "")
    .slice(0, 10);
}

function companyToForm(company) {
  if (!company) return { ...EMPTY_FORM };
  return {
    companyName: company.name || "",
    address: company.address || "",
    email: company.email || "",
    mobile: company.phone || "",
    pfCode: company.pfCode || "",
    esiCode: company.esiCode || "",
    labourLicense: company.labourLicense || "",
    domainName: company.domainName || "",
    contactPerson: company.contactPerson || "",
    website: company.website || "",
    panNo: company.panNo || "",
    tanNo: company.tanNo || "",
    password: "",
    logo: company.logo || null,
    headOfOrganisationEmployeeId: company.headOfOrganisationEmployeeId ?? null,
  };
}

function validateForm(form, { requireTan = false } = {}) {
  const errors = {};
  if (!String(form.companyName || "").trim()) {
    errors.companyName = "Company name is required";
  }
  if (!String(form.email || "").trim()) {
    errors.email = "Email is required";
  }
  const tan = cleanTan(form.tanNo);
  if (requireTan && !tan) {
    errors.tanNo = "TAN is required for Form 24Q filing";
  } else if (tan && !TAN_REGEX.test(tan)) {
    errors.tanNo = "TAN must be 4 letters + 5 digits + 1 letter (e.g. BLRM12345E)";
  }
  const pan = cleanPan(form.panNo);
  if (pan && !PAN_REGEX.test(pan)) {
    errors.panNo = "PAN must be 5 letters + 4 digits + 1 letter (e.g. ABCDE1234F)";
  }
  return errors;
}

/** Persist deductor fields on the company record (used by Enterprise + Form 24Q). */
export async function saveCompanyDeductorDetails({
  companyId,
  tanNo,
  panNo,
  name,
  address,
  email,
  phone,
  contactPerson,
}) {
  const id = companyId || getAuthJson("currentUser", null)?.companyId;
  if (!id) throw new Error("Company id not found");

  const tan = cleanTan(tanNo);
  const pan = cleanPan(panNo);
  if (tan && !TAN_REGEX.test(tan)) {
    throw new Error("Invalid TAN format. Expected 4 letters + 5 digits + 1 letter (e.g. BLRM12345E).");
  }
  if (pan && !PAN_REGEX.test(pan)) {
    throw new Error("Invalid PAN format. Expected 5 letters + 4 digits + 1 letter (e.g. ABCDE1234F).");
  }

  const payload = {
    tanNo: tan || null,
    panNo: pan || null,
  };
  if (name != null && String(name).trim()) payload.name = String(name).trim();
  if (address != null) payload.address = address || null;
  if (email != null && String(email).trim()) payload.email = String(email).trim();
  if (phone != null && String(phone).trim()) payload.phone = String(phone).trim();
  if (contactPerson != null) payload.contactPerson = contactPerson || null;

  const response = await companyService.updateCompanyData(id, payload);
  if (!response?.success || !response.company) {
    throw new Error(response?.message || "Failed to save deductor details");
  }
  setAuthJson("companyData", response.company);
  return response.company;
}

function ActionButton({ children, onClick, disabled, variant = "primary", type = "button" }) {
  const base =
    "inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium transition disabled:opacity-50";
  const styles =
    variant === "primary"
      ? "bg-redhalo-red text-white hover:opacity-90"
      : variant === "navy"
        ? "bg-redhalo-navy text-white hover:opacity-90"
        : "border border-[hsl(var(--ds-border))] bg-white text-[hsl(var(--ds-fg))] hover:bg-[hsl(var(--ds-muted))]";
  return (
    <button type={type} className={`${base} ${styles}`} onClick={onClick} disabled={disabled}>
      {children}
    </button>
  );
}

function employeeLabel(emp) {
  if (!emp) return "";
  const name = [emp.user?.name, emp.user?.lastName].filter(Boolean).join(" ").trim();
  return `${emp.empCode || emp.id}${name ? ` - ${name}` : ""}`;
}

export default function Enterprise({ darkMode }) {
  const { hasModuleAccess, hasScreenAccess, isAdmin } = usePermissions();
  const canEdit =
    isAdmin ||
    hasModuleAccess("configuration", "edit") ||
    hasScreenAccess("configuration", "enterprise", "edit");

  const [formData, setFormData] = useState({ ...EMPTY_FORM });
  const [baseline, setBaseline] = useState({ ...EMPTY_FORM });
  const [companyId, setCompanyId] = useState(null);
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [employeeOptions, setEmployeeOptions] = useState([]);
  const [hoChangeConfirm, setHoChangeConfirm] = useState(null);
  const [moveDirectReports, setMoveDirectReports] = useState(false);

  const mapAndSet = useCallback((company) => {
    const mapped = companyToForm(company);
    setFormData(mapped);
    setBaseline(mapped);
    setFieldErrors({});
    if (company?.id) setCompanyId(company.id);
  }, []);

  const fetchEmployees = useCallback(async () => {
    try {
      const response = await employeeService.getEmployees({ limit: 5000 });
      const list = Array.isArray(response?.data)
        ? response.data
        : Array.isArray(response?.employees)
          ? response.employees
          : [];
      setEmployeeOptions(
        list.map((item) => ({
          value: item.id,
          label: employeeLabel(item),
          raw: item,
        }))
      );
    } catch (err) {
      console.error("Failed to load employees for HO picker:", err);
    }
  }, []);

  const fetchCompanyData = useCallback(
    async ({ silent = false } = {}) => {
      setLoading(true);
      try {
        const user = getAuthJson("currentUser", null);
        const id = user?.companyId;
        if (!id) {
          const cached = getAuthJson("companyData", null);
          if (cached) mapAndSet(cached);
          if (!silent) toast.error("Unable to resolve company");
          return;
        }
        setCompanyId(id);
        const response = await companyService.getCompanyData(id, { skipCache: true });
        if (response?.success && response.company) {
          setAuthJson("companyData", response.company);
          mapAndSet(response.company);
          if (!silent) toast.success("Enterprise details loaded from server");
        } else {
          const cached = getAuthJson("companyData", null);
          if (cached) mapAndSet(cached);
          if (!silent) toast.error(response?.message || "Failed to load company");
        }
      } catch (error) {
        console.error("Error fetching company data:", error);
        const cached = getAuthJson("companyData", null);
        if (cached) mapAndSet(cached);
        if (!silent) toast.error(error?.message || "Error fetching company data");
      } finally {
        setLoading(false);
      }
    },
    [mapAndSet]
  );

  useEffect(() => {
    fetchCompanyData({ silent: true });
    fetchEmployees();
  }, [fetchCompanyData, fetchEmployees]);

  const onChange = (name, value) => {
    let next = value;
    if (name === "tanNo") next = cleanTan(value);
    if (name === "panNo") next = cleanPan(value);
    setFormData((prev) => ({ ...prev, [name]: next }));
    if (fieldErrors[name]) {
      setFieldErrors((prev) => {
        const copy = { ...prev };
        delete copy[name];
        return copy;
      });
    }
  };

  const handleCancel = () => {
    setFormData(baseline);
    setFieldErrors({});
    setEditing(false);
    setHoChangeConfirm(null);
    setMoveDirectReports(false);
  };

  const previousHoLabel = useMemo(() => {
    const id = baseline.headOfOrganisationEmployeeId;
    if (!id) return "";
    return employeeOptions.find((o) => String(o.value) === String(id))?.label || `Employee #${id}`;
  }, [baseline.headOfOrganisationEmployeeId, employeeOptions]);

  const newHoLabel = useMemo(() => {
    const id = formData.headOfOrganisationEmployeeId;
    if (!id) return "";
    return employeeOptions.find((o) => String(o.value) === String(id))?.label || `Employee #${id}`;
  }, [formData.headOfOrganisationEmployeeId, employeeOptions]);

  const persistCompany = async ({ movePreviousHoDirectReportsToNewHo = false } = {}) => {
    const id = companyId || getAuthJson("currentUser", null)?.companyId;
    if (!id) {
      toast.error("Company id not found");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        name: formData.companyName.trim(),
        address: formData.address || null,
        email: formData.email.trim(),
        phone: formData.mobile || null,
        pfCode: formData.pfCode || null,
        esiCode: formData.esiCode || null,
        labourLicense: formData.labourLicense || null,
        domainName: formData.domainName || null,
        contactPerson: formData.contactPerson || null,
        website: formData.website || null,
        panNo: cleanPan(formData.panNo) || null,
        tanNo: cleanTan(formData.tanNo) || null,
        headOfOrganisationEmployeeId: formData.headOfOrganisationEmployeeId || null,
        movePreviousHoDirectReportsToNewHo,
      };
      if (formData.password && formData.password.trim()) {
        payload.password = formData.password.trim();
      }

      const response = await companyService.updateCompanyData(id, payload);
      if (response?.success && response.company) {
        setAuthJson("companyData", response.company);
        mapAndSet(response.company);
        setEditing(false);
        setHoChangeConfirm(null);
        setMoveDirectReports(false);
        toast.success("Enterprise details saved — Form 24Q will use this TAN");
      } else {
        toast.error(response?.message || "Save failed");
      }
    } catch (error) {
      console.error("Error saving company:", error);
      toast.error(error?.message || "Error saving enterprise details");
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    if (!canEdit) {
      toast.error("You do not have permission to edit enterprise details");
      return;
    }
    const errors = validateForm(formData);
    const prevHo = baseline.headOfOrganisationEmployeeId
      ? Number(baseline.headOfOrganisationEmployeeId)
      : null;
    const nextHo = formData.headOfOrganisationEmployeeId
      ? Number(formData.headOfOrganisationEmployeeId)
      : null;
    if (prevHo && !nextHo) {
      errors.headOfOrganisationEmployeeId =
        "Cannot clear Head of Organisation. Select a replacement instead.";
    }
    setFieldErrors(errors);
    if (Object.keys(errors).length) {
      toast.error(Object.values(errors)[0]);
      return;
    }

    // Changing HO: confirm previous HO reports to new HO + optional move directs
    if (prevHo && nextHo && prevHo !== nextHo) {
      setMoveDirectReports(false);
      setHoChangeConfirm({ prevHo, nextHo });
      return;
    }

    await persistCompany();
  };

  const readOnly = !editing;

  return (
    <div className="ds-page-panel min-h-0 flex-1 overflow-y-auto">
      <div className="ds-page-panel-head flex flex-wrap items-start justify-between gap-3">
        <DsPageHeader
          title="Enterprise Details"
          infoTooltip={
            editing
              ? "Editing company profile. TAN is used as the Form 24Q deductor TAN."
              : "Company profile from your organization record. Use Edit to update fields including TAN for Form 24Q."
          }
        />
        <div className="flex flex-wrap items-center gap-2">
          <ActionButton
            variant="outline"
            disabled={loading || saving || editing}
            onClick={() => fetchCompanyData({ silent: false })}
          >
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
            Refresh
          </ActionButton>
          {canEdit && !editing && (
            <ActionButton variant="primary" disabled={loading} onClick={() => setEditing(true)}>
              <Pencil size={14} />
              Edit
            </ActionButton>
          )}
          {canEdit && editing && (
            <>
              <ActionButton variant="outline" disabled={saving} onClick={handleCancel}>
                <X size={14} />
                Cancel
              </ActionButton>
              <ActionButton variant="navy" disabled={saving} onClick={handleSave}>
                <Save size={14} />
                {saving ? "Saving…" : "Save"}
              </ActionButton>
            </>
          )}
        </div>
      </div>

      <div className="space-y-6 p-5 lg:p-6">
        <section>
          <h3 className="mb-3 text-sm font-semibold text-[hsl(var(--ds-fg))]">
            Organization profile
          </h3>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
            <InputField
              label="Company Name"
              name="companyName"
              value={formData.companyName}
              readOnly={readOnly}
              error={fieldErrors.companyName}
              onChange={onChange}
            />
            <InputField
              label="Address"
              name="address"
              value={formData.address}
              readOnly={readOnly}
              onChange={onChange}
            />
            <InputField
              label="Email ID"
              name="email"
              value={formData.email}
              readOnly={readOnly}
              error={fieldErrors.email}
              onChange={onChange}
            />
            <InputField
              label="Mobile Number"
              name="mobile"
              value={formData.mobile}
              readOnly={readOnly}
              onChange={onChange}
            />
            <InputField
              label="PF Code No."
              name="pfCode"
              value={formData.pfCode}
              readOnly={readOnly}
              onChange={onChange}
            />
            <InputField
              label="ESI Code No."
              name="esiCode"
              value={formData.esiCode}
              readOnly={readOnly}
              onChange={onChange}
            />
            <InputField
              label="Labour License No."
              name="labourLicense"
              value={formData.labourLicense}
              readOnly={readOnly}
              onChange={onChange}
            />
            <InputField
              label="Domain Name"
              name="domainName"
              value={formData.domainName}
              readOnly={readOnly}
              onChange={onChange}
            />
            <InputField
              label="Website"
              name="website"
              value={formData.website}
              readOnly={readOnly}
              onChange={onChange}
            />
            <InputField
              label="Company PAN"
              name="panNo"
              value={formData.panNo}
              readOnly={readOnly}
              error={fieldErrors.panNo}
              onChange={onChange}
              placeholder="ABCDE1234F"
              hint="Format: 5 letters + 4 digits + 1 letter"
            />
            <InputField
              label="TAN (Tax Deduction Account No.)"
              name="tanNo"
              value={formData.tanNo}
              readOnly={readOnly}
              error={fieldErrors.tanNo}
              onChange={onChange}
              placeholder="BLRM12345E"
              hint="Format: 4 letters + 5 digits + 1 letter (used for Form 24Q)"
            />
            <div>
              {readOnly ? (
                <>
                  <DsLabel htmlFor="enterprise-headOfOrganisationEmployeeId">
                    Head of Organisation
                  </DsLabel>
                  <DsInput
                    id="enterprise-headOfOrganisationEmployeeId"
                    value={
                      formData.headOfOrganisationEmployeeId
                        ? employeeOptions.find(
                            (o) =>
                              String(o.value) ===
                              String(formData.headOfOrganisationEmployeeId)
                          )?.label || `Employee #${formData.headOfOrganisationEmployeeId}`
                        : ""
                    }
                    readOnly
                    disabled
                    placeholder="Not set"
                  />
                </>
              ) : (
                <SearchableSelect
                  id="headOfOrganisationEmployeeId"
                  label="Head of Organisation"
                  value={formData.headOfOrganisationEmployeeId}
                  onChange={(e) =>
                    onChange(
                      "headOfOrganisationEmployeeId",
                      e?.target?.value === "" || e?.target?.value == null
                        ? null
                        : e.target.value
                    )
                  }
                  staticOptions={employeeOptions}
                  darkMode={darkMode}
                  isEmployee={false}
                  required={false}
                  error={fieldErrors.headOfOrganisationEmployeeId}
                  disabled={false}
                  compact
                  placeholder="Select Head of Organisation"
                />
              )}
              <p className="mt-1 text-[11px] text-[hsl(var(--ds-muted-fg))]">
                This employee has no Reporting Manager (organisation root). One per company.
              </p>
            </div>
            {editing && (
              <InputField
                label="Password (leave blank to keep)"
                name="password"
                value={formData.password}
                readOnly={false}
                onChange={onChange}
                type="password"
                placeholder="••••••••"
              />
            )}

            {formData.logo && (
              <div className="flex items-center gap-4 lg:col-span-2">
                <img
                  src={formData.logo}
                  alt="Company Logo"
                  className="h-20 w-20 rounded-lg border border-[hsl(var(--ds-border))] object-contain"
                />
                <span className="ds-body text-[hsl(var(--ds-muted-fg))]">Logo on file</span>
              </div>
            )}
          </div>
        </section>

        {!canEdit && (
          <p className="text-xs text-[hsl(var(--ds-muted-fg))]">
            You have view-only access. Ask an admin for{" "}
            <strong>Configuration → Enterprise → Edit</strong> permission to change TAN and other
            fields.
          </p>
        )}
      </div>

      {hoChangeConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] p-5 shadow-[var(--ds-shadow-card)]">
            <h4 className="ds-h4 mb-2">Change Head of Organisation?</h4>
            <p className="ds-body mb-3 text-[hsl(var(--ds-muted-fg))]">
              Previous head <strong className="text-[hsl(var(--ds-fg))]">{previousHoLabel}</strong>{" "}
              will report to{" "}
              <strong className="text-[hsl(var(--ds-fg))]">{newHoLabel}</strong>. The new head&apos;s
              Reporting Manager will be cleared.
            </p>
            <label className="mb-4 flex items-start gap-2 text-sm text-[hsl(var(--ds-fg))]">
              <input
                type="checkbox"
                className="mt-0.5"
                checked={moveDirectReports}
                onChange={(e) => setMoveDirectReports(e.target.checked)}
              />
              <span>
                Move {previousHoLabel || "previous head"}&apos;s direct reports to{" "}
                {newHoLabel || "new head"}
              </span>
            </label>
            <div className="flex justify-end gap-2">
              <ActionButton
                variant="outline"
                disabled={saving}
                onClick={() => {
                  setHoChangeConfirm(null);
                  setMoveDirectReports(false);
                }}
              >
                Cancel
              </ActionButton>
              <ActionButton
                variant="navy"
                disabled={saving}
                onClick={() =>
                  persistCompany({
                    movePreviousHoDirectReportsToNewHo: moveDirectReports,
                  })
                }
              >
                {saving ? "Saving…" : "Confirm & Save"}
              </ActionButton>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function InputField({
  label,
  name,
  value,
  readOnly,
  onChange,
  error,
  hint,
  placeholder,
  type = "text",
}) {
  return (
    <div>
      <DsLabel htmlFor={`enterprise-${name}`}>{label}</DsLabel>
      <DsInput
        id={`enterprise-${name}`}
        name={name}
        type={type}
        value={value || ""}
        readOnly={readOnly}
        disabled={readOnly}
        placeholder={placeholder || label}
        onChange={(e) => onChange?.(name, e.target.value)}
        className={error ? "border-red-500" : undefined}
        autoComplete={type === "password" ? "new-password" : "off"}
      />
      {error ? (
        <p className="mt-1 text-[11px] text-red-600">{error}</p>
      ) : hint && !readOnly ? (
        <p className="mt-1 text-[11px] text-[hsl(var(--ds-muted-fg))]">{hint}</p>
      ) : null}
    </div>
  );
}
